import {
  CLAVES_DE_FILTRO,
  FILTRO_SIN_EXPORTAR,
  FILTRO_TODOS,
  type FiltrosDeConsulta,
  type FiltrosDeInventario,
} from '@cnc/contracts';
import {useEffect, useState, type ReactNode} from 'react';
import {tr} from '../i18n/idioma';
import {mes, numero} from '../utilidades/formato';
import {AYUDA_FILTROS_INVENTARIO} from './ayuda-inventario';
import {NotaDeAyuda} from './nota-de-ayuda';
import {SelectorMultiple, type OpcionDeFiltro} from './selector-multiple';

type Props = {
  consulta: FiltrosDeConsulta;
  filtros: FiltrosDeInventario | undefined;
  onCambiar: (cambios: Partial<FiltrosDeConsulta>) => void;
  onLimpiar: () => void;
  // Filtros de las columnas agregadas a la tabla (7/10/2026), debajo de los fijos.
  porColumna?: ReactNode;
};

type Opcion = OpcionDeFiltro;

// Los que van bajo "Más filtros".
const FILTROS_SECUNDARIOS: Array<keyof FiltrosDeConsulta> = [
  'proveedor',
  'mina',
  'planta',
  'ubicacion',
  'recoleccion',
  'mesMolienda',
  'etdDesde',
  'etdHasta',
  'etaDesde',
  'etaHasta',
  'origen',
];

// Panel de filtros del inventario. Las listas vienen encadenadas del servidor:
// cada una muestra solo lo que existe con los demas filtros puestos.
export function FiltrosInventario({consulta, filtros, onCambiar, onLimpiar, porColumna}: Props) {
  const [busqueda, setBusqueda] = useState(consulta.buscar ?? '');

  // La busqueda espera a que se deje de escribir antes de consultar.
  useEffect(() => {
    const temporizador = setTimeout(() => {
      if ((consulta.buscar ?? '') !== busqueda.trim()) onCambiar({buscar: busqueda.trim() || undefined});
    }, 350);
    return () => clearTimeout(temporizador);
  }, [busqueda, consulta.buscar, onCambiar]);
  useEffect(() => setBusqueda(consulta.buscar ?? ''), [consulta.buscar]);

  const activos = CLAVES_DE_FILTRO.filter(clave => consulta[clave] !== undefined && consulta[clave] !== '').length;
  // Arriba solo los principales; "Más filtros" despliega los demas (abierto si alguno esta puesto).
  const ocultosActivos = FILTROS_SECUNDARIOS.filter(
    clave => consulta[clave] !== undefined && consulta[clave] !== '',
  ).length;
  const [mas, setMas] = useState(ocultosActivos > 0);
  const opciones = (
    lista: Array<{valor: string; cantidad: number}> | undefined,
    etiqueta?: (v: string) => string,
  ): Opcion[] =>
    (lista ?? []).map(o => ({valor: o.valor, etiqueta: etiqueta ? etiqueta(o.valor) : o.valor, cantidad: o.cantidad}));
  const etiquetaDeMes = (sinTexto: string) => (v: string) => (v === 'sin' ? sinTexto : mes(v));

  // Inventario de CNC y de Mineros por separado, o los dos juntos (decision 0022).
  const lineas = filtros?.lineas ?? [];
  const opcionesDeLinea: Array<{valor: FiltrosDeConsulta['linea']; etiqueta: string; cantidad?: number}> = [
    {valor: undefined, etiqueta: tr('Unificado', 'Unified'), cantidad: lineas.reduce((s, l) => s + l.cantidad, 0)},
    ...lineas.map(l => ({valor: l.valor, etiqueta: l.etiqueta, cantidad: l.cantidad})),
  ];

  const campoFecha = 'mt-0.5 w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta';
  return (
    <div className="rounded-lg border border-linea bg-white px-3 py-2">
      <div
        className="mb-2 flex flex-wrap items-center gap-1.5 text-sm"
        role="group"
        aria-label={tr('Inventario', 'Inventory')}
      >
        <span className="mr-1 text-xs text-apagado">
          {tr('Inventario:', 'Inventory:')}
          <NotaDeAyuda titulo={tr('Inventario', 'Inventory')} texto={AYUDA_FILTROS_INVENTARIO.linea} />
        </span>
        {opcionesDeLinea.map(o => (
          <button
            key={o.valor ?? 'todos'}
            type="button"
            aria-pressed={consulta.linea === o.valor}
            onClick={() => onCambiar({linea: o.valor})}
            className={`rounded-full border px-3 py-0.5 text-xs ${
              consulta.linea === o.valor
                ? 'border-verde bg-verde-claro text-verde-oscuro'
                : 'border-linea text-tinta hover:bg-papel'
            }`}
          >
            {o.etiqueta}
            {o.cantidad !== undefined && <span className="ml-1 text-apagado">({numero(o.cantidad)})</span>}
          </button>
        ))}
      </div>
      <div className="grid items-end gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <label className="block text-xs text-apagado sm:col-span-2">
          {tr('Buscar', 'Search')}
          <NotaDeAyuda texto={AYUDA_FILTROS_INVENTARIO.buscar} dentroDeEtiqueta />
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={tr(
              'BB #, exportación, sello, contenedor o recolección',
              'BB #, export, seal, container or pickup',
            )}
            className="mt-0.5 w-full rounded-md border border-linea px-3 py-1 text-sm text-tinta"
          />
        </label>
        <SelectorMultiple
          etiqueta={tr('Estado', 'Status')}
          ayuda={AYUDA_FILTROS_INVENTARIO.estado}
          // Sin ninguno marcado se guarda estado=todos: sin estado la tabla abre con disponibles y asignados.
          valor={
            consulta.estado === FILTRO_TODOS
              ? undefined
              : consulta.estado === FILTRO_SIN_EXPORTAR
                ? 'available|assigned'
                : consulta.estado
          }
          opciones={(filtros?.estados ?? []).map(e => ({valor: e.valor, etiqueta: e.etiqueta, cantidad: e.cantidad}))}
          onCambiar={v => onCambiar({estado: v ?? FILTRO_TODOS})}
        />
        <SelectorMultiple
          etiqueta={tr('Exportador', 'Exporter')}
          ayuda={AYUDA_FILTROS_INVENTARIO.exportador}
          valor={consulta.exportador}
          opciones={opciones(filtros?.exportadores)}
          onCambiar={v => onCambiar({exportador: v})}
        />
        {/* Desplegable con las exportaciones de las bolsas filtradas (6/10/2026); una parte del código se busca en "Buscar". */}
        <SelectorMultiple
          etiqueta={tr('Exportación', 'Export')}
          ayuda={AYUDA_FILTROS_INVENTARIO.exportacion}
          valor={consulta.exportacion}
          opciones={opciones(filtros?.exportaciones, v => (v === 'sin' ? tr('Sin exportación', 'No export') : v))}
          onCambiar={v => onCambiar({exportacion: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Mes de zarpe (ETD)', 'Departure month (ETD)')}
          ayuda={AYUDA_FILTROS_INVENTARIO.mesEtd}
          valor={consulta.mesEtd}
          opciones={opciones(filtros?.mesesEtd, etiquetaDeMes(tr('Sin fecha de zarpe', 'No departure date')))}
          onCambiar={v => onCambiar({mesEtd: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Mes de llegada (ETA)', 'Arrival month (ETA)')}
          ayuda={AYUDA_FILTROS_INVENTARIO.mesEta}
          valor={consulta.mesEta}
          opciones={opciones(filtros?.mesesEta, etiquetaDeMes(tr('Sin fecha de llegada', 'No arrival date')))}
          onCambiar={v => onCambiar({mesEta: v})}
        />
        <button
          type="button"
          aria-expanded={mas}
          onClick={() => setMas(v => !v)}
          className="rounded-md border border-linea bg-white px-2 py-1 text-xs text-tinta hover:bg-papel"
        >
          {mas ? tr('▴ Menos filtros', '▴ Fewer filters') : tr('▾ Más filtros', '▾ More filters')}
          {ocultosActivos > 0 && <span className="ml-1 font-semibold text-verde-oscuro">({ocultosActivos})</span>}
        </button>
      </div>
      {mas && (
        <div className="mt-2 grid items-end gap-2 border-t border-linea/70 pt-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <SelectorMultiple
            etiqueta={tr('Proveedor', 'Supplier')}
            ayuda={AYUDA_FILTROS_INVENTARIO.proveedor}
            valor={consulta.proveedor}
            opciones={opciones(filtros?.proveedores)}
            onCambiar={v => onCambiar({proveedor: v})}
          />
          <SelectorMultiple
            etiqueta={tr('Mina', 'Mine')}
            ayuda={AYUDA_FILTROS_INVENTARIO.mina}
            valor={consulta.mina}
            opciones={opciones(filtros?.minas)}
            onCambiar={v => onCambiar({mina: v})}
          />
          <SelectorMultiple
            etiqueta={tr('Planta', 'Plant')}
            ayuda={AYUDA_FILTROS_INVENTARIO.planta}
            valor={consulta.planta}
            opciones={opciones(filtros?.plantas)}
            onCambiar={v => onCambiar({planta: v})}
          />
          <SelectorMultiple
            etiqueta={tr('Bodega', 'Warehouse')}
            ayuda={AYUDA_FILTROS_INVENTARIO.ubicacion}
            valor={consulta.ubicacion}
            opciones={opciones(filtros?.ubicaciones)}
            onCambiar={v => onCambiar({ubicacion: v})}
          />
          <SelectorMultiple
            etiqueta={tr('Recolección', 'Pickup')}
            ayuda={AYUDA_FILTROS_INVENTARIO.recoleccion}
            valor={consulta.recoleccion}
            opciones={opciones(filtros?.recolecciones)}
            onCambiar={v => onCambiar({recoleccion: v})}
          />
          <SelectorMultiple
            etiqueta={tr('Mes de molienda', 'Grinding month')}
            ayuda={AYUDA_FILTROS_INVENTARIO.mesMolienda}
            valor={consulta.mesMolienda}
            opciones={opciones(filtros?.mesesMolienda, etiquetaDeMes(tr('Sin mes de molienda', 'No grinding month')))}
            onCambiar={v => onCambiar({mesMolienda: v})}
          />
          <label className="block text-xs text-apagado">
            {tr('Zarpe desde', 'Departure from')}
            <NotaDeAyuda texto={AYUDA_FILTROS_INVENTARIO.etdDesde} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etdDesde ?? ''}
              onChange={e => onCambiar({etdDesde: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Zarpe hasta', 'Departure to')}
            <NotaDeAyuda texto={AYUDA_FILTROS_INVENTARIO.etdHasta} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etdHasta ?? ''}
              onChange={e => onCambiar({etdHasta: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Llegada desde', 'Arrival from')}
            <NotaDeAyuda texto={AYUDA_FILTROS_INVENTARIO.etaDesde} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etaDesde ?? ''}
              onChange={e => onCambiar({etaDesde: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Llegada hasta', 'Arrival to')}
            <NotaDeAyuda texto={AYUDA_FILTROS_INVENTARIO.etaHasta} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etaHasta ?? ''}
              onChange={e => onCambiar({etaHasta: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
          {(filtros?.origenes.length ?? 0) > 1 && (
            <SelectorMultiple
              etiqueta={tr('Archivo de origen', 'Source file')}
              ayuda={AYUDA_FILTROS_INVENTARIO.origen}
              valor={consulta.origen}
              opciones={(filtros?.origenes ?? []).map(o => ({
                valor: o.valor,
                etiqueta: o.etiqueta,
                cantidad: o.cantidad,
              }))}
              // Los dos archivos marcados es lo mismo que ninguno.
              onCambiar={v => onCambiar({origen: v?.includes('|') ? undefined : (v as FiltrosDeConsulta['origen'])})}
            />
          )}
        </div>
      )}
      {porColumna}
      {activos > 0 && (
        <div className="mt-2 flex items-center justify-between text-xs text-apagado">
          <span>
            {tr(
              `${activos} filtro${activos === 1 ? '' : 's'} activo${activos === 1 ? '' : 's'}`,
              `${activos} active filter${activos === 1 ? '' : 's'}`,
            )}
          </span>
          <button type="button" onClick={onLimpiar} className="text-verde hover:underline">
            {tr('Quitar filtros', 'Clear filters')}
          </button>
        </div>
      )}
    </div>
  );
}
